<template>
  <base-drawer
    class="drawer-form"
    :show="show"
    :title="isEdit ? 'Edit rating scale' : 'Add rating scale'"
    width="480px"
    @close="onClose"
  >
    <drawer-section title="Details" icon="icon-notes">
      <base-input
        label="Name"
        placeholder="Difficulty"
        v-model="form.name"
        :error="errs.name"
      />
      <base-input label="Display" class="mb-0">
        <segmented-toggle v-model="form.display_type" :options="displayOptions" size="sm" variant="pills" />
      </base-input>
      <info-hint v-if="isEdit && ratedCount > 0" class="mt-3" variant="neutral" icon="icon-alert-circle-exc">
        {{ ratedCount }} {{ ratedCount === 1 ? 'event is' : 'events are' }} rated on this scale. Changes that would remove a rating in use are blocked.
      </info-hint>
    </drawer-section>

    <drawer-section v-if="form.display_type === 'icons'" title="Icons" icon="icon-shape-star">
      <base-input class="form-field form-field--compact" label="Number of icons" :error="errs.max_value">
        <el-input-number v-model="form.max_value" size="small" :min="MIN_ICONS" :max="MAX_ICONS" :step="1" :precision="0" />
      </base-input>

      <div class="mb-3">
        <el-switch active-color="#e14eca" v-model="form.allow_half" />
        <label class="text-white"> &nbsp; Allow half ratings
          <el-popover trigger="hover" placement="right">
            <div>
              <div class="popover-body">Lets you rate an event 3.5 out of 5.</div>
            </div>
            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
          </el-popover>
        </label>
      </div>

      <base-input label="Icon" :error="errs.icon">
        <el-select v-model="form.icon" class="select-primary" filterable>
          <el-option
            v-for="option in iconOptions"
            :key="option.value"
            class="select-primary"
            :label="iconLabel(option.name)"
            :value="option.value"
          >
            <span style="float: left; display: flex; align-items: center;">
              <b-icon v-if="option.type === 'bootstrap'" :icon="option.value" scale="1" class="mr-1" color="#e14eca"></b-icon>
              <i v-else :class="'tim-icons mr-1 ' + option.value" style="color: #e14eca; font-size: 1.1em;"></i>
              {{ iconLabel(option.name) }}
            </span>
          </el-option>
        </el-select>
      </base-input>

      <base-input label="Color" :error="errs.color">
        <el-color-picker v-model="form.color" show-alpha />
      </base-input>

      <label>Preview</label>
      <div>
        <rating-display :value="previewValue" :scale="previewScale" size="lg" />
      </div>
    </drawer-section>

    <drawer-section v-else title="Levels" icon="icon-bullet-list-67">
      <info-hint variant="neutral" icon="icon-bulb-63">
        List levels from lowest to highest. Renaming or reordering a level keeps every event on it.
      </info-hint>
      <draggable v-model="form.levels" tag="div" handle=".data-table__handle" :animation="150">
        <div v-for="(level, index) in form.levels" :key="level.key" class="d-flex align-items-start mt-2">
          <span class="data-table__handle mt-2 mr-1" title="Drag to reorder" aria-label="Drag to reorder">
            <i class="tim-icons el-icon-rank"></i>
          </span>
          <base-input
            class="flex-grow-1 mb-0"
            :placeholder="'Level ' + (index + 1)"
            v-model="level.name"
            :error="errs['levels[' + index + ']']"
          />
          <button
            type="button"
            class="row-remove mt-2 ml-1"
            :title="'Remove ' + (level.name.trim() || 'level')"
            :aria-label="'Remove ' + (level.name.trim() || 'level')"
            :disabled="form.levels.length <= MIN_LEVELS"
            @click="removeLevel(index)"
          >
            <i class="tim-icons icon-simple-remove"></i>
          </button>
        </div>
      </draggable>
      <p v-if="errs.levels" class="text-danger small mt-2 mb-0">{{ errs.levels }}</p>
      <base-button class="mt-3" variant="outlined" tone="primary" size="sm" :disabled="form.levels.length >= MAX_LEVELS" @click="addLevel">
        <i class="tim-icons icon-simple-add"></i> Add level
      </base-button>

      <base-input class="mt-3" label="Color" :error="errs.color">
        <el-color-picker v-model="form.color" show-alpha />
      </base-input>

      <label>Preview</label>
      <div class="tags-row">
        <span
          v-for="level in form.levels.filter(l => l.name.trim())"
          :key="level.key"
          class="tag-chip text-white"
          :style="tagChipStyle(form.color)"
        >{{ level.name.trim() }}</span>
      </div>
    </drawer-section>

    <base-alert-dialog
      :show="dialog.show"
      variant="warning"
      :title="dialog.title"
      :message="dialog.message"
      confirm-text="OK"
      @confirm="dialog.show = false"
      @cancel="dialog.show = false"
    />

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="saving" @click="save">
          <i v-if="saving" class="el-icon-loading mr-1"></i>{{ isEdit ? 'Save' : 'Add scale' }}
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import { ColorPicker } from 'element-ui'
import draggable from 'vuedraggable'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { RATING_ICON_OPTIONS, MIN_ICONS, MAX_ICONS, MIN_LEVELS, MAX_LEVELS, iconLabel, ratingPreviewValue } from '@/util/ratings'
import { tagChipStyle } from '@/util/tagChip'

const props = defineProps({
  show: { type: Boolean, default: false },
  scale: { type: Object, default: null },
})

const emit = defineEmits(['update:show', 'close', 'saved'])

const { proxy } = getCurrentInstance() as any

// el-color-picker is not globally registered; register it for this template.
const ElColorPicker = ColorPicker

const displayOptions = [
  { value: 'icons', label: 'Icons' },
  { value: 'labels', label: 'Named levels' },
]
const iconOptions = RATING_ICON_OPTIONS

const isEdit = computed(() => !!(props.scale && props.scale.id))
const ratedCount = computed(() => Number(props.scale?.rated_event_count || 0))

interface LevelRow { key: number, name: string, previous_position: number | null }
let levelKey = 0

function newLevel(name = '', previous: number | null = null): LevelRow {
  levelKey += 1
  return { key: levelKey, name, previous_position: previous }
}

function defaultForm() {
  return {
    name: '',
    display_type: 'icons',
    max_value: 5,
    allow_half: false,
    icon: 'star',
    color: '#e14eca',
    levels: [newLevel('Beginner'), newLevel('Intermediate'), newLevel('Advanced')] as LevelRow[],
  }
}

const form = ref(defaultForm())
const errs = ref<Record<string, string>>({})
const saving = ref(false)
const dialog = ref({ show: false, title: '', message: '' })

const previewValue = computed(() => ratingPreviewValue({ max_value: form.value.max_value, allow_half: form.value.allow_half ? 1 : 0 }))

const previewScale = computed(() => ({
  display_type: 'icons',
  max_value: form.value.max_value,
  icon: form.value.icon,
  color: form.value.color,
}))

function loadForm() {
  errs.value = {}
  const s = props.scale
  if (s && s.id) {
    form.value = {
      name: s.name || '',
      display_type: s.display_type,
      max_value: s.display_type === 'icons' ? Number(s.max_value) : 5,
      allow_half: !!s.allow_half,
      icon: s.icon || 'star',
      color: s.color || '#e14eca',
      levels: s.display_type === 'labels' && s.levels.length
        ? s.levels.map((name: string, i: number) => newLevel(name, i + 1))
        : defaultForm().levels,
    }
  } else {
    form.value = defaultForm()
  }
}

function removeLevel(index: number) {
  form.value.levels = form.value.levels.filter((_, i) => i !== index)
}

function addLevel() {
  form.value.levels = [...form.value.levels, newLevel()]
}

function validate() {
  const e: Record<string, string> = {}
  if (!form.value.name.trim()) e.name = 'Name is required'
  if (form.value.display_type === 'labels') {
    const seen = new Set<string>()
    form.value.levels.forEach((level, i) => {
      const name = level.name.trim()
      if (!name) e['levels[' + i + ']'] = 'Level name is required'
      else if (seen.has(name.toLowerCase())) e['levels[' + i + ']'] = 'Level names must be unique'
      seen.add(name.toLowerCase())
    })
    if (form.value.levels.length < MIN_LEVELS || form.value.levels.length > MAX_LEVELS) {
      e.levels = `Add between ${MIN_LEVELS} and ${MAX_LEVELS} levels`
    }
  }
  errs.value = e
  return Object.keys(e).length === 0
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function baseUrl() {
  const companyId = localStorage.getItem('companyId')
  const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
  return `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/rating-scales`
}

function errorBody(err: any) {
  const data = err?.response?.data
  if (typeof data === 'string') {
    try { return JSON.parse(data) } catch (e) { return { error: data } }
  }
  return data || {}
}

async function save() {
  if (!validate()) return
  saving.value = true
  try {
    const config = await authConfig()
    const isLabels = form.value.display_type === 'labels'
    const payload: Record<string, any> = {
      name: form.value.name.trim(),
      display_type: form.value.display_type,
      max_value: isLabels ? form.value.levels.length : form.value.max_value,
      allow_half: !isLabels && form.value.allow_half ? 1 : 0,
      icon: form.value.icon,
      color: form.value.color,
    }
    if (isLabels) {
      payload.levels = form.value.levels.map(l => ({ name: l.name.trim(), previous_position: l.previous_position }))
    }
    if (isEdit.value) {
      payload.archived = props.scale.archived ? 1 : 0
      await axios.put(`${baseUrl()}/${props.scale.id}`, payload, config)
    } else {
      await axios.post(baseUrl(), payload, config)
    }
    proxy.$notify({ type: 'success', message: isEdit.value ? 'Rating scale saved' : 'Rating scale added', icon: 'tim-icons icon-check-2' })
    emit('saved')
    onClose()
  } catch (err: any) {
    const status = err?.response?.status
    const body = errorBody(err)
    if (status === 409) {
      const count = Number(body.in_use_conflict_count || 0)
      dialog.value = {
        show: true,
        title: 'Ratings in use',
        message: `${count} ${count === 1 ? 'event uses' : 'events use'} a rating this change would remove. Update those events first, or keep the current setting.`,
      }
    } else if (status === 400 && body.validation_errors) {
      const { scales, ...fields } = body.validation_errors
      errs.value = fields
      if (scales) dialog.value = { show: true, title: 'Could not save', message: scales }
    } else {
      dialog.value = { show: true, title: 'Could not save', message: 'The rating scale could not be saved. Please try again.' }
    }
  } finally {
    saving.value = false
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && !dialog.value.show) onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    loadForm()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>
